import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconCheck, IconEdit } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';

interface BudgetLine {
  id: number;
  org_unit: { id: number; unit_name: string } | null;
  chart_of_account: { id: number; code: string; name: string };
  type: string;
  project_name: string | null;
  amount: string;
  notes: string | null;
  revised_amount: number;
  committed_amount: number;
  actual_amount: number;
}

interface Adjustment {
  id: number;
  type: 'reappropriation' | 'supplementary';
  status: 'draft' | 'approved';
  amount: string;
  reason: string;
  from_budget_line: { id: number; chart_of_account: { code: string; name: string } } | null;
  to_budget_line: { id: number; chart_of_account: { code: string; name: string } };
  created_by: { id: number; full_name: string } | null;
  approved_by: { id: number; full_name: string } | null;
}

interface BudgetRecord {
  id: number;
  status: 'draft' | 'approved';
  financial_year: { id: number; name: string };
  created_by: { id: number; full_name: string } | null;
  approved_by: { id: number; full_name: string } | null;
  lines: BudgetLine[];
  adjustments: Adjustment[];
}

interface AdjustmentFormState {
  type: 'reappropriation' | 'supplementary';
  from_budget_line_id: string;
  to_budget_line_id: string;
  amount: string;
  reason: string;
}

const emptyAdjustment: AdjustmentFormState = { type: 'reappropriation', from_budget_line_id: '', to_budget_line_id: '', amount: '', reason: '' };

export function BudgetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [budget, setBudget] = useState<BudgetRecord | null>(null);
  const [adjustmentForm, setAdjustmentForm] = useState(emptyAdjustment);
  const [adjustmentErrors, setAdjustmentErrors] = useState<Record<string, string[]>>({});
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  const canCreate = can('budgets.create');
  const canConfigure = can('budgets.configure');
  const fmtMoney = (n: number | string) => formatMoney(Number(n), settings.currency);

  const load = () => api.get<BudgetRecord>(`/budgets/${id}`).then((res) => setBudget(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const approve = async () => {
    if (!(await confirmAction('Approve this budget? It becomes the authoritative budget for its financial year.', { confirmLabel: 'Approve' }))) return;
    try {
      await api.post(`/budgets/${id}/approve`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'You may not approve a budget you prepared yourself.');
    }
  };

  const approveAdjustment = async (adjustment: Adjustment) => {
    if (!(await confirmAction('Approve this adjustment?', { confirmLabel: 'Approve' }))) return;
    try {
      await api.post(`/budget-adjustments/${adjustment.id}/approve`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'You may not approve an adjustment you raised yourself.');
    }
  };

  const submitAdjustment = async (e: FormEvent) => {
    e.preventDefault();
    setSavingAdjustment(true);
    setAdjustmentErrors({});
    try {
      await api.post(`/budgets/${id}/adjustments`, {
        type: adjustmentForm.type,
        from_budget_line_id: adjustmentForm.type === 'reappropriation' ? adjustmentForm.from_budget_line_id : undefined,
        to_budget_line_id: adjustmentForm.to_budget_line_id,
        amount: adjustmentForm.amount,
        reason: adjustmentForm.reason,
      });
      setAdjustmentForm(emptyAdjustment);
      load();
    } catch (err) {
      setAdjustmentErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    } finally {
      setSavingAdjustment(false);
    }
  };

  if (!budget) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to budgets" onClick={() => navigate('/budgets')} />
          <h1>{budget.financial_year.name} Budget</h1>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${budget.status === 'approved' ? 'badge-active' : 'badge-neutral'}`}>
            {budget.status === 'approved' ? 'Approved' : 'Draft'}
          </span>
          {canCreate && budget.status === 'draft' && (
            <IconButton icon={IconEdit} label="Edit" to={`/budgets/${budget.id}/edit`} />
          )}
          {canConfigure && budget.status === 'draft' && (
            <button type="button" className="btn btn-primary" onClick={approve}>
              Approve
            </button>
          )}
        </div>
      </header>

      <div className="card">
        <div className="detail-grid">
          <div>
            <h3>Prepared By</h3>
            <p>{budget.created_by?.full_name ?? '—'}</p>
          </div>
          <div>
            <h3>Approved By</h3>
            <p>{budget.approved_by?.full_name ?? '—'}</p>
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Lines</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Cost Centre</th>
                <th>Account</th>
                <th>Type</th>
                <th className="right">Budget</th>
                <th className="right">Revised</th>
                <th className="right">Committed</th>
                <th className="right">Actual</th>
              </tr>
            </thead>
            <tbody>
              {budget.lines.map((line) => (
                <tr key={line.id}>
                  <td className="muted">{line.org_unit?.unit_name ?? 'Fund-wide'}</td>
                  <td>
                    {line.chart_of_account.code} — {line.chart_of_account.name}
                  </td>
                  <td className="muted">
                    {line.type === 'capital' ? 'Capital' : 'Operational'}
                    {line.project_name ? ` (${line.project_name})` : ''}
                  </td>
                  <td className="right">{fmtMoney(line.amount)}</td>
                  <td className="right">{fmtMoney(line.revised_amount)}</td>
                  <td className="right">{fmtMoney(line.committed_amount)}</td>
                  <td className="right">{fmtMoney(line.actual_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {budget.status === 'approved' && (
        <div className="card">
          <h2>Adjustments</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th className="right">Amount</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {budget.adjustments.map((adj) => (
                  <tr key={adj.id}>
                    <td>{adj.type === 'reappropriation' ? 'Re-appropriation' : 'Supplementary'}</td>
                    <td className="muted">
                      {adj.from_budget_line ? `${adj.from_budget_line.chart_of_account.code} — ${adj.from_budget_line.chart_of_account.name}` : '—'}
                    </td>
                    <td>
                      {adj.to_budget_line.chart_of_account.code} — {adj.to_budget_line.chart_of_account.name}
                    </td>
                    <td className="right">{fmtMoney(adj.amount)}</td>
                    <td className="muted">{adj.reason}</td>
                    <td>
                      <span className={`badge ${adj.status === 'approved' ? 'badge-active' : 'badge-neutral'}`}>
                        {adj.status === 'approved' ? 'Approved' : 'Draft'}
                      </span>
                    </td>
                    <td className="right">
                      {canConfigure && adj.status === 'draft' && (
                        <IconButton icon={IconCheck} label="Approve" onClick={() => approveAdjustment(adj)} />
                      )}
                    </td>
                  </tr>
                ))}
                {budget.adjustments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted center">
                      No adjustments yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {canCreate && (
            <form onSubmit={submitAdjustment} className="form-grid">
              <h3 className="span-2">New adjustment</h3>
              {adjustmentErrors.general && <div className="alert alert-error span-2">{adjustmentErrors.general[0]}</div>}
              <label>
                Type
                <select
                  value={adjustmentForm.type}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, type: e.target.value as 'reappropriation' | 'supplementary' })}
                >
                  <option value="reappropriation">Re-appropriation</option>
                  <option value="supplementary">Supplementary</option>
                </select>
              </label>
              <label>
                Amount
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={adjustmentForm.amount}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                  required
                />
                {adjustmentErrors.amount && <small className="error">{adjustmentErrors.amount[0]}</small>}
              </label>
              {adjustmentForm.type === 'reappropriation' && (
                <label>
                  From line
                  <select
                    value={adjustmentForm.from_budget_line_id}
                    onChange={(e) => setAdjustmentForm({ ...adjustmentForm, from_budget_line_id: e.target.value })}
                    required
                  >
                    <option value="">Select…</option>
                    {budget.lines.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.chart_of_account.code} — {l.chart_of_account.name}
                      </option>
                    ))}
                  </select>
                  {adjustmentErrors.from_budget_line_id && <small className="error">{adjustmentErrors.from_budget_line_id[0]}</small>}
                </label>
              )}
              <label>
                To line
                <select
                  value={adjustmentForm.to_budget_line_id}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, to_budget_line_id: e.target.value })}
                  required
                >
                  <option value="">Select…</option>
                  {budget.lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.chart_of_account.code} — {l.chart_of_account.name}
                    </option>
                  ))}
                </select>
                {adjustmentErrors.to_budget_line_id && <small className="error">{adjustmentErrors.to_budget_line_id[0]}</small>}
              </label>
              <label className="span-2">
                Reason
                <input
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm({ ...adjustmentForm, reason: e.target.value })}
                  required
                  maxLength={1000}
                />
                {adjustmentErrors.reason && <small className="error">{adjustmentErrors.reason[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary" disabled={savingAdjustment}>
                  {savingAdjustment ? 'Saving…' : 'Raise adjustment'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
}
